import { queryCache, useQuery } from 'react-query';
import { authService } from '../index';
import { httpGetAsync, httpPostAsync } from '../utils/HttpClient';
import { readCardAnswer, type CardAnswer } from '../payments/cardAnswer';
import type {
  CardAction,
  DownPaymentCardActionResult,
  DownPaymentCardCharge,
  DownPaymentCardChargeRequest,
  DownPaymentCardConfig,
} from '../payments/downPaymentCards';

// LOS card down payments (spec A2, A6, A8, A10-A13). These calls use HttpClient's async helpers rather than fetchData:
// they hand back the raw Response, so a refusal's status and `{ error, errors }` reach the user, and a request that got
// no response at all is told apart from a refusal (a lost charge answer must not be charged again). fetchData only
// reads, and throws away the error body.

const get = async <T>(url: string): Promise<CardAnswer<T>> =>
  readCardAnswer<T>(await httpGetAsync(url, (await authService.getToken())!));

const post = async <T>(url: string, body: unknown): Promise<CardAnswer<T>> =>
  readCardAnswer<T>(await httpPostAsync(url, body, (await authService.getToken())!));

/** A2: card mode for the Down Payment dialog. */
export const getDownPaymentCardConfig = (applicationId: string, locationOfSaleId: string) =>
  get<DownPaymentCardConfig>(
    `api/DownPayment/CardConfig?applicationId=${encodeURIComponent(applicationId)}&locationOfSaleId=${encodeURIComponent(locationOfSaleId)}`,
  );

/** A6/A7: charges the card and records the deposit. An answer without a row is treated as lost. */
export const chargeDownPaymentCard = async (request: DownPaymentCardChargeRequest): Promise<CardAnswer<DownPaymentCardCharge>> => {
  const answer = await post<DownPaymentCardCharge>('api/DownPayment/ChargeCard', request);
  return answer.kind === 'ok' && !answer.data?.status ? { kind: 'lost' } : answer;
};

/** A8: the charge a lost answer was for; refused with 404 when nothing was recorded. */
export const getDownPaymentCardChargeByRequest = (clientRequestId: string) =>
  get<DownPaymentCardCharge>(`api/DownPayment/CardCharge?clientRequestId=${encodeURIComponent(clientRequestId)}`);

const ACTION_ROUTES: Record<CardAction, string> = {
  Check: 'Check',
  RetryPosting: 'RetryPosting',
  RecordReceipt: 'RecordReceipt',
  Resolve: 'Resolve',
  RetryReversal: 'RetryReversal',
};

/** A10, A11: one action on a card row. `RecordReceipt` takes `{ receipt }`, `Resolve` takes `{ note }`. */
export const runDownPaymentCardAction = (ledgerId: string, action: CardAction, body: { receipt?: string; note?: string } = {}) =>
  post<DownPaymentCardActionResult>(`api/DownPayment/CardCharges/${encodeURIComponent(ledgerId)}/${ACTION_ROUTES[action]}`, body);

const cardChargesKey = (applicationId: string): [string, string] => ['DownPaymentCardCharges', applicationId];

/** A13: the application's card rows, newest first. Throws the refusal's text so react-query keeps the last good list. */
export const listDownPaymentCardCharges = async (applicationId: string): Promise<DownPaymentCardCharge[]> => {
  const answer = await get<DownPaymentCardCharge[]>(`api/DownPayment/CardCharges?applicationId=${encodeURIComponent(applicationId)}`);
  if (answer.kind === 'ok') return answer.data ?? [];
  throw new Error(answer.kind === 'refused' ? answer.messages.join(' ') : 'Card payments could not be read.');
};

/**
 * The application's card rows, shared by the details card's bar, Down Payment History, the dialog and the Card payments
 * window through react-query's cache, so one refresh updates them all.
 */
export const useDownPaymentCardCharges = (applicationId: string | undefined) =>
  useQuery(
    // No key, no read: react-query 1 waits until there is an application.
    applicationId ? cardChargesKey(applicationId) : null,
    () => listDownPaymentCardCharges(applicationId!),
    { refetchOnWindowFocus: false, staleTime: 30_000, retry: 1 },
  );

/** Reads the application's card rows again (after a charge, an action or a void). */
export const refreshDownPaymentCardCharges = (applicationId: string | undefined) =>
  applicationId ? queryCache.refetchQueries(cardChargesKey(applicationId), { force: true }) : Promise.resolve();

/** Reads every cached application's card rows again (after a void, which knows only the deposit). */
export const refreshAllDownPaymentCardCharges = () =>
  queryCache.refetchQueries(['DownPaymentCardCharges'], { force: true });

/** Reads the rows now for a decision (posting), and keeps the shared cache in step. */
export const fetchDownPaymentCardChargesNow = async (applicationId: string): Promise<DownPaymentCardCharge[]> => {
  const charges = await listDownPaymentCardCharges(applicationId);
  queryCache.setQueryData(cardChargesKey(applicationId), charges);
  return charges;
};
